import { FC, useMemo } from 'react';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';
import { formatNumber } from 'utils/formats';
import { dateFormat } from 'constants/formats';
import { IncentiveOffer, IncentiveOfferStatus } from 'modules/incentivesMarket/types';

type IncentiveOfferStatusBannerProps = {
  offer: IncentiveOffer;
  offerStatus: IncentiveOfferStatus;
};

const IncentiveOfferStatusBanner: FC<IncentiveOfferStatusBannerProps> = ({
  offer,
  offerStatus,
}) => {
  const { t } = useTranslate();

  const agreedShares = offer.unitsCount.agreed;
  const totalShares = offer.unitsCount.total;
  const availableShares = totalShares - agreedShares;
  const isPartiallyAccepted = agreedShares > 0 && agreedShares < totalShares;

  const progressPercent = useMemo(() => {
    if (totalShares === 0) return 0;
    return Math.round((agreedShares / totalShares) * 100);
  }, [agreedShares, totalShares]);

  if (offerStatus === 'EXPIRED') {
    return (
      <div className="alert alert-warning alert-icon mb-4">
        <em className="icon ni ni-alert-circle" />
        <div>
          <h6 className="alert-heading">{t('ExpiredOffer')}</h6>
          <p className="mb-0">{t('ExpiredOfferDescription')}</p>
        </div>
      </div>
    );
  }

  if (offerStatus === 'ACCEPTED') {
    return (
      <div className="alert alert-info alert-icon mb-4">
        <em className="icon ni ni-check-circle" />
        <div>
          <h6 className="alert-heading">{t('FullyAccepted')}</h6>
          <p className="mb-0">
            {t('FullyAcceptedDescription', {
              total: formatNumber(totalShares),
            })}
          </p>
        </div>
      </div>
    );
  }

  if (isPartiallyAccepted) {
    return (
      <div className="alert alert-info alert-icon mb-4">
        <em className="icon ni ni-pie" />
        <div>
          <h6 className="alert-heading">
            {t('PartiallyAccepted')} ({progressPercent}%)
          </h6>
          <p className="mb-0">
            {t('PartiallyAcceptedDescription', {
              agreed: formatNumber(agreedShares),
              total: formatNumber(totalShares),
            })}
            {'. '}
            {t('Remaining')}: <strong>{formatNumber(availableShares)}</strong>{' '}
            {t('IncentivesMarketUnits').toLowerCase()}{' '}
            {t('Until').toLowerCase()}{' '}
            <strong>{format(new Date(offer.endDate), dateFormat)}</strong>.
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default IncentiveOfferStatusBanner;
